import { DiscussionContext } from "@cnx/common";
import { Box, Text } from "@mantine/core";
import { useDisclosure, useElementSize, useMergedRef } from "@mantine/hooks";
import type { TFunction } from "i18next";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DateFormat from "types/DateFormat";
import { dateToUTC } from "utils/date";

export interface ChatTitleProps {
  title?: string;
  discussionContext?: DiscussionContext;
}

const joinNonEmpty = (...parts: (string | null | undefined)[]): string =>
  parts.filter(Boolean).join(", ");

const buildContextTitle = (
  ctx: DiscussionContext,
  prefix: string,
  fallback: string,
  t: TFunction<"discussions">
): string => {
  switch (ctx.context) {
    case "resourceCombination": {
      const content = joinNonEmpty(
        ctx.time ? dateToUTC(ctx.time).format(DateFormat.DEFAULT) : null,
        ctx.tripId || fallback,
        ctx.trailer ? `${t("trailer")}: ${ctx.trailer}` : null,
        ctx.truck ? `${t("truck")}: ${ctx.truck}` : null,
        ctx.driver ? `${t("driver")}: ${ctx.driver}` : null
      );
      return `${prefix}: ${content}`;
    }
    case "stop": {
      const address = [
        ctx.locationAddress,
        ctx.locationZipCode,
        ctx.locationCityName,
      ]
        .filter(Boolean)
        .join(" ");
      const content = joinNonEmpty(
        ctx.locationName || fallback,
        address || null,
        ctx.countryCode
      );
      return `${prefix}: ${content}`;
    }
    case "salesOrderLeg": {
      const date = ctx.plannedDateTime
        ? dateToUTC(ctx.plannedDateTime).format(DateFormat.DEFAULT)
        : null;
      const content = joinNonEmpty(
        date || ctx.bookingId || fallback,
        ctx.palletGroupDescription || null,
        ctx.productDescription || null,
        ctx.sender ? `${t("sender")}: ${ctx.sender}` : null,
        ctx.receiver ? `${t("receiver")}: ${ctx.receiver}` : null
      );
      return `${prefix}: ${content}`;
    }
    case "address": {
      const address = [
        ctx.locationZipCode,
        ctx.locationCityName,
        ctx.countryCode,
      ]
        .filter(Boolean)
        .join(" ");
      return joinNonEmpty(ctx.locationName || fallback, address);
    }
    default:
      return fallback;
  }
};

const ChatTitle: FC<ChatTitleProps> = ({ title = "", discussionContext }) => {
  const { t } = useTranslation("discussions");
  const [opened, { toggle }] = useDisclosure(false);
  const [hasSpoiler, setHasSpoiler] = useState(false);
  const elementRef = useRef<HTMLParagraphElement>(null);
  const {
    ref: sizeRef,
    height,
    width,
  } = useElementSize<HTMLParagraphElement>();
  const textRef = useMergedRef(elementRef, sizeRef);

  const hasContext = !!discussionContext?.context;

  const prefixMap: Record<string, string> = {
    resourceCombination: t("trip"),
    stop: t("stop"),
    salesOrderLeg: t("shipment"),
    address: "",
  };

  const prefix = hasContext ? prefixMap[discussionContext!.context!] ?? "" : "";
  const fullTitle = hasContext
    ? buildContextTitle(discussionContext!, prefix, title, t)
    : title;

  useEffect(() => {
    if (!elementRef.current || !hasContext) return;
    setHasSpoiler(height + 1 < elementRef.current.scrollHeight);
  }, [height, width, hasContext]);

  if (!hasContext) {
    return (
      <Text fz="md" c="var(--color-gray-dark)" fw={700}>
        {title}
      </Text>
    );
  }

  return (
    <Box className="relative">
      <Text
        ref={textRef}
        lh={1.2}
        fz="md"
        c="var(--color-gray-dark)"
        fw={600}
        lineClamp={opened ? undefined : 2}
      >
        {fullTitle}
        {opened && (
          <>
            {" "}
            <Text
              component="span"
              fz="xs"
              c="var(--color-gray)"
              className="cursor-pointer"
              ml={4}
              style={{ whiteSpace: "nowrap" }}
              onClick={() => {
                toggle();
              }}
            >
              &#91;{t("showLess").toLocaleLowerCase()}&#93;
            </Text>
          </>
        )}
      </Text>
      {!opened && hasSpoiler && (
        <Box
          component="span"
          className="absolute bottom-0 right-0 cursor-pointer flex items-center"
          style={{ height: "1.2em", background: "white", paddingLeft: 8 }}
          onClick={() => {
            toggle();
          }}
        >
          <Text
            component="span"
            fz="xs"
            c="var(--color-blue) "
            style={{ whiteSpace: "nowrap" }}
          >
            &#91;{t("showMore").toLocaleLowerCase()}&#93;
          </Text>
        </Box>
      )}
    </Box>
  );
};

export default ChatTitle;
